import katalMetricsPublisher from 'src/metrics/katalMetricsPublisher';

type ChildActionPublisher = ReturnType<typeof katalMetricsPublisher.newChildActionPublisherForMethod>;

/**
 * Centralizes the "create a child action publisher, publish monitors, and never let a
 * metrics/logging failure escape" pattern shared by every Seller University metrics publisher
 * (page-performance, user-interaction, and API-duration). Consolidating it here keeps the
 * error-handling contract consistent — in particular the nested catch that guards against the
 * logger itself throwing — as more publishers are added.
 *
 * @param methodName - Action id passed to `newChildActionPublisherForMethod` (e.g. `${page}.PageLoad`).
 * @param publish - Callback that publishes monitors on the created child publisher.
 * @param onError - Site-specific failure handler (typically `ClientLogger.warn`). Any error it
 *   throws is swallowed so metric/logging failures never affect application functionality.
 */
export const safePublish = (
  methodName: string,
  publish: (publisher: ChildActionPublisher) => void,
  onError: (error: unknown) => void,
): void => {
  try {
    const publisher = katalMetricsPublisher.newChildActionPublisherForMethod(methodName);
    publish(publisher);
  } catch (error) {
    try {
      onError(error);
    } catch { /* never propagate metric/logging errors */ }
  }
};

export default safePublish;
